import type { PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useMemo } from 'react';
import type { AggregateCollectorStatus } from '@elfsight/widget-fetcher';
import { useAggregateFetcher } from '@elfsight/widget-fetcher';
import type { SortingType } from '../../types';
import { useTabs } from '../tabs';
import { useSocialFeedSettings } from '../utils';
import type { FetcherType } from './determineFetcher';
import { determineFetcher } from './determineFetcher';
import { usePreparedSource } from './PreparedSourceProvider';
import type { DataValue, ResponseType } from './sources';
import { useDataLimit } from './useDataLimit';
import {
  clearSortCache,
  stableRandomSort,
  useUpdatedTwitterData
} from './utils';

type CollectorStatus =
  | 'idle'
  | 'pending'
  | 'rejected'
  | 'failed'
  | 'success'
  | 'exhausted'
  | 'limitReached';

type SourceStateType = {
  latestStatus: CollectorStatus;
  latestHTTPStatus: number | undefined;
  latestError: Error | undefined;
  latestRawData: ResponseType | undefined;
};

type AggregateFetcherType = {
  id: string;
  data: DataValue[];
  status: AggregateCollectorStatus;
  hasNext: boolean;
  next(): void;
  sourcesStates?: SourceStateType[];
};

type DataContextValue = AggregateFetcherType & {
  isLoading: boolean;
};

const DataContext = createContext<DataContextValue>({
  id: '',
  data: [],
  sourcesStates: [],
  status: 'pending',
  hasNext: false,
  next: () => {},
  isLoading: true
});

const orderFunctionsMap: Record<
  SortingType,
  (items: DataValue[]) => DataValue[]
> = {
  random: stableRandomSort,
  date: (items) =>
    [...items].sort((itemA, itemB) => {
      const timestampA = itemA.timestamp ?? 0;
      const timestampB = itemB.timestamp ?? 0;

      return timestampB - timestampA;
    })
};

export function DataProvider({ children }: PropsWithChildren) {
  const { sort, widgetToken, tabsGrouping } = useSocialFeedSettings();
  const { preparedSources, sourcesReady, updateSourceAuthor } =
    usePreparedSource();
  const { currentTab } = useTabs();

  // Очищаем кеш рандомной сортировки при смене типа сортировки
  useEffect(() => {
    if (sort === 'random') {
      clearSortCache();
    }
  }, [sort]);

  const order = orderFunctionsMap[sort];

  const fetcherSources = preparedSources.reduce<FetcherType[]>(
    (acc, source) => {
      const fetcher = determineFetcher(
        source,
        [sort],
        widgetToken,
        updateSourceAuthor
      );

      if (fetcher) {
        acc.push(fetcher);
      }

      return acc;
    },
    []
  );

  const aggregateFetcherValue = useAggregateFetcher(
    // @ts-expect-error В useAggregateFetcher неточные типы
    fetcherSources,
    {
      sliceLength: 10,
      groupBySourceType: tabsGrouping
    }
  ) as AggregateFetcherType[];

  const currentAggregateFetcher = aggregateFetcherValue.find(
    (fetcher) => fetcher.id === currentTab.id
  );
  const { id, data, hasNext, next, status, sourcesStates } =
    currentAggregateFetcher || aggregateFetcherValue[0];

  const { isLoadingTwitterData, updatedData } = useUpdatedTwitterData(data);

  const isLoading =
    isLoadingTwitterData ||
    !sourcesReady ||
    (sourcesReady && fetcherSources.length > 0 && status === 'pending');

  const orderedData = useMemo(() => {
    const items = updatedData as DataValue[];

    return order ? order(items) : items;
  }, [order, updatedData]);

  const value = useMemo<DataContextValue>(
    () => ({
      id,
      data: orderedData,
      hasNext: orderedData.length > 0 && hasNext,
      next,
      isLoading,
      status,
      sourcesStates
    }),
    [id, orderedData, hasNext, next, isLoading, status, sourcesStates]
  );

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useData() {
  const limit = useDataLimit();
  const dataContext = useContext(DataContext);

  return useMemo(() => {
    if (limit === undefined || limit <= 0) {
      return dataContext;
    }

    const limitedData = dataContext.data.slice(0, limit);

    return {
      ...dataContext,
      data: limitedData,
      hasNext: limitedData.length < limit && dataContext.hasNext
    };
  }, [dataContext, limit]);
}
